import { useState, useEffect, useRef, useCallback } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils' // tailwind classNames 合并工具，可用 classnames 包或自己写
import { ChevronUp, ChevronDown } from 'lucide-react'
import { isNil } from 'lodash'

interface InputNumberProps {
  value?: number | null
  onChange: (val: number | null) => void
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  precision?: number
  style?: React.CSSProperties
  className?: string
  readOnly?: boolean
}

// 格式化数字，包括货币、百分比
const formatter = (precision = 0) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })
}

export function InputNumber(props: InputNumberProps) {
  const {
    value,
    onChange,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    step = 1,
    disabled = false,
    precision = 0,
    style,
    className,
    readOnly = false,
  } = props
  const [displayValue, setDisplayValue] = useState('')

  const formatNumber = useCallback(
    (num?: number | null) => {
      if (isNil(num) || Number.isNaN(num)) return ''
      return formatter(precision).format(num)
    },
    [precision],
  )

  const parseNumber = (str: string): number | null => {
    if (!str) return null
    const normalized = str.replace(/,/g, '')
    const parsed = Number.parseFloat(normalized)
    if (Number.isNaN(parsed)) return null
    return parsed
  }

  useEffect(() => {
    setDisplayValue(formatNumber(value))
  }, [value, formatNumber])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    if (disabled || readOnly) return
    if (/^[\d,\.\-]*$/.test(val)) {
      setDisplayValue(val)
      const parsed = parseNumber(val)
      if (parsed !== null) {
        let v = Math.min(max, Math.max(min, parsed))
        v = Number(v.toFixed(precision))
        onChange(v)
      } else {
        onChange(null)
      }
    }
  }

  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const changeValue = (delta: number) => {
    let newVal = (value ?? 0) + delta
    newVal = Math.min(max, Math.max(min, Number(newVal.toFixed(precision))))
    onChange(newVal)
  }

  const handleMouseDown = (delta: number) => {
    if (disabled || readOnly) return
    changeValue(delta)
    intervalRef.current = setInterval(() => changeValue(delta), 150)
  }

  const handleMouseUp = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled || readOnly) return
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      changeValue(step)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      changeValue(-step)
    }
  }

  return (
    <div
      className={cn(
        'inline-flex items-stretch border border-input rounded-md select-none',
        disabled ? 'cursor-not-allowed opacity-60' : '',
        className,
      )}
      style={{ ...style }}
      aria-disabled={disabled}
    >
      <Input
        type="text"
        className="bg-accent"
        readOnly={readOnly}
        inputMode="decimal"
        disabled={disabled}
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      {/* 右侧按钮容器 */}
      <div
        className={cn(
          'flex flex-col border-l border-[rgba(255,255,255,0.2)] select-none bg-accent',
        )}
      >
        {/* 增加按钮 */}
        <button
          type="button"
          aria-label="Increase value"
          disabled={disabled || (!isNil(value) && value >= max)}
          onMouseDown={() => handleMouseDown(step)}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          className={cn(
            'flex-1 flex items-center justify-center border-binput text-accent-foreground',
            disabled || (!isNil(value) && value >= max)
              ? 'text-gray-300 cursor-not-allowed hover:bg-transparent active:bg-transparent'
              : 'cursor-pointer',
          )}
        >
          <ChevronUp size={16} />
        </button>

        {/* 减少按钮 */}
        <button
          type="button"
          aria-label="Decrease value"
          disabled={disabled || (!isNil(value) && value <= min)}
          onMouseDown={() => handleMouseDown(-step)}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          className={cn(
            'flex-1 flex items-center justify-center text-accent-foreground',
            disabled || (!isNil(value) && value <= min)
              ? 'text-gray-300 cursor-not-allowed hover:bg-transparent active:bg-transparent'
              : 'cursor-pointer',
          )}
        >
          <ChevronDown size={16} />
        </button>
      </div>
    </div>
  )
}
